import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoArrowBack, IoCheckmarkCircle, IoAdd, IoTrash, IoInformationCircle } from "react-icons/io5";

const VendorAddProductScreen = () => {
  const navigate = useNavigate();
  const fileInputRefs = useRef([]);
  const timeoutRef = useRef();

  const [images, setImages] = useState(Array(9).fill(null));
  const [imageFiles, setImageFiles] = useState(Array(9).fill(null));
  const [productName, setProductName] = useState("");
  const [category, setCategory] = useState("");
  const [color, setColor] = useState("");
  const [description, setDescription] = useState("");
  const [size, setSize] = useState("");
  const [weight, setWeight] = useState("");
  const [quantity, setQuantity] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [note, setNote] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [gender, setGender] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImages = [...images];
        newImages[index] = e.target.result;
        setImages(newImages);
      };
      reader.readAsDataURL(file);

      const newFiles = [...imageFiles];
      newFiles[index] = file;
      setImageFiles(newFiles);
    }
  };

  const handleImageRemove = (index) => {
    const newImages = [...images];
    newImages[index] = null;
    setImages(newImages);
    const newFiles = [...imageFiles];
    newFiles[index] = null;
    setImageFiles(newFiles);
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].value = "";
    }
  };

  const handleSlotClick = (index) => {
    fileInputRefs.current[index]?.click();
  };

  const isProductInfoComplete = 
    images.some((img) => img !== null) &&
    productName.trim() !== "" &&
    category.trim() !== "" &&
    color.trim() !== "" &&
    description.trim() !== "";

  const isVariationComplete = 
    size.trim() !== "" &&
    weight.trim() !== "" &&
    quantity.trim() !== "" &&
    sellingPrice.trim() !== "";

  const isSpecificationComplete = 
    note.trim() !== "" &&
    ageGroup.trim() !== "" &&
    gender.trim() !== "";

  const uploadImagesToCloudinary = async () => {
    const selectedFiles = imageFiles.filter(Boolean);
    if (selectedFiles.length === 0) {
      return { urls: [], error: "Please select at least one image." };
    }

    try {
      const uploads = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "buyit-picture-uploads");
        const res = await axios.post(
          "https://api.cloudinary.com/v1_1/dwyx1u5on/image/upload",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return res.data.secure_url;
      });

      const urls = await Promise.all(uploads);
      return { urls, error: "" };
    } catch (err) {
      return { urls: [], error: "Image upload failed. Please try again." };
    }
  };

  const handleAddProduct = async () => {
    if (isSubmitting) return;
    setSubmitError("");
    setIsSubmitting(true);

    const { urls, error } = await uploadImagesToCloudinary();
    if (error) {
      setSubmitError(error);
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem("vendorToken");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const payload = {
        productImages: urls,
        productName: productName.trim(),
        productCategory: category.trim(),
        productColor: color.trim(),
        productDescription: description.trim(),
        productSize: size.trim(),
        productWeight: weight.trim(),
        productQuantity: Number(quantity),
        productSellingPrice: Number(sellingPrice),
        productNote: note.trim(),
        ageGroup: ageGroup.trim(),
        gender: gender.trim(),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/products/`,
        payload,
        { headers }
      );

      if (response?.data?.success) {
        setShowSuccess(true);
        timeoutRef.current = setTimeout(() => {
          navigate("/products");
        }, 2000);
      } else {
        const message = response?.data?.message || "Failed to submit product.";
        setSubmitError(message);
        
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to submit product.";
      setSubmitError(message);

    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseOverlay = () => {
    setShowSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex flex-col items-center">
            <div className="h-12 w-12 rounded-full border-4 border-[#19C463]/30 border-t-[#19C463] animate-spin" />
            <p className="mt-3 text-sm font-semibold text-white">Adding Product...</p>
          </div>
        </div>
      )}
      {showSuccess && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={handleCloseOverlay}
        >
          <div 
            className="mx-4 max-w-sm rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-center text-base font-medium text-gray-900">
              Your product has been sent to our team for review and we will let you know when product is live!
            </p>
          </div>
        </div>
      )}
      

      <header className="fixed top-0 left-0 right-0 z-10 flex items-center gap-4 bg-white px-6 py-4 shadow-sm">
        <button onClick={() => navigate(-1)} className="text-gray-900">
          <IoArrowBack className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">Add Product</h1>
      </header>

      <main className="px-6 pt-20 pb-6">
        <div className="mx-auto max-w-2xl space-y-6">
          <section className="rounded-lg bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Product Information</h2>
              <IoCheckmarkCircle
                className={`h-6 w-6 ${isProductInfoComplete ? "text-[#19C463]" : "text-[#AAAAAA]"}`}
              />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              {images.map((image, index) => (
                <div key={index} className="relative">
                  <input
                    type="file"
                    ref={(el) => (fileInputRefs.current[index] = el)}
                    onChange={(e) => handleImageUpload(index, e)}
                    accept="image/*"
                    className="hidden"
                  />
                  {image ? (
                    <div className="relative aspect-square">
                      <img
                        loading="lazy"
                        src={image}
                        alt={`Product ${index + 1}`}
                        onClick={() => handleSlotClick(index)}
                        className="h-full w-full cursor-pointer rounded-lg object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageRemove(index);
                        }}
                        className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow-md"
                      >
                        <IoTrash className="h-4 w-4 text-[#19C463]" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => handleSlotClick(index)}
                      className="relative flex aspect-square cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-[#19C463] bg-white"
                    >
                      <IoAdd className="h-8 w-8 text-[#19C463]" />
                      <span className="mt-1 text-xs text-[#19C463]">Main Image</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-lg bg-green-50 p-3">
              <IoInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#19C463]" />
              <div>
                <p className="text-sm text-gray-700">Click on the image again to change the picture</p>
                <p className="mt-1 text-xs text-gray-600">
                  Upload up to 9 images. Only 1 image is required. Supported formats: JPG, PNG. Max size: 5MB each.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Product Name</label>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Enter product name"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                >
                  <option value="">Select</option>
                  <option value="Baby Care">Baby Care</option>
                  <option value="Fashion & Beauty">Fashion & Beauty</option>
                  <option value="Personal Cares">Personal Cares</option>
                  <option value="Electronics & Gadgets">Electronics & Gadgets</option>
                  <option value="Phones & Accessories">Phones & Accessories</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Color</label>
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="Enter color"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Product Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter product description"
                  rows="3"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                />
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Product Variation</h2>
              <IoCheckmarkCircle
                className={`h-6 w-6 ${isVariationComplete ? "text-[#19C463]" : "text-[#AAAAAA]"}`}
              />
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Size</label>
                <select
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                >
                  <option value="">Select</option>
                  <option value="Small">Small</option>
                  <option value="Medium">Medium</option>
                  <option value="Big">Big</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Weight</label>
                <div className="relative">
                  <input
                    type="number"
                    value={weight}
                    onChange={e => {
                      // Only allow numbers and decimals
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val)) setWeight(val);
                    }}
                    placeholder="kg"
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                  />
                  {weight && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm select-none pointer-events-none">kg</span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Quantity</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Selling Price</label>
                <input
                  type="text"
                  value={sellingPrice ? Number(sellingPrice.replace(/,/g, '')).toLocaleString() : ''}
                  onChange={e => {
                    // Remove non-digit characters
                    const raw = e.target.value.replace(/[^\d]/g, '');
                    setSellingPrice(raw);
                  }}
                  placeholder="Enter selling price"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Product Specification</h2>
              <IoCheckmarkCircle
                className={`h-6 w-6 ${isSpecificationComplete ? "text-[#19C463]" : "text-[#AAAAAA]"}`}
              />
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Note</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Enter note"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Age Group</label>
                <select
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                >
                  <option value="">Select age group</option>
                  <option value="0-12 Years">0-12 Years</option>
                  <option value="13-17 Years">13-17 Years</option>
                  <option value="18-24 Years">18-24 Years</option>
                  <option value="25+ Years">25+ Years</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Gender</label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Unisex">Unisex</option>
                </select>
              </div>
            </div>
          </section>

          <button
            onClick={handleAddProduct}
            className={`w-full rounded-lg py-3 text-sm font-semibold transition-colors 
              ${isProductInfoComplete && isVariationComplete && isSpecificationComplete ? 'bg-[#19C463] text-white active:bg-[#14a352]' : 'bg-gray-300 text-gray-400 cursor-not-allowed'}`}
            disabled={!(isProductInfoComplete && isVariationComplete && isSpecificationComplete)}
          >
            Add Product
          </button>
          {submitError && (
            <p className="mt-3 text-center text-sm font-medium text-red-600">
              {submitError}
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default VendorAddProductScreen;
